import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiService } from '../services/apiService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';

interface ProfileOptions {
  businessUnit: string | null;
  locale: string | null;
  businessUnits: string[];
  locales: { code: string; label: string; country: string }[];
}

function errorMessage(error: unknown) {
  if (error instanceof Error) {
    const start = error.message.indexOf('{');
    if (start >= 0) {
      try {
        const problem = JSON.parse(error.message.slice(start)) as { title?: string };
        if (problem.title) return problem.title;
      } catch { /* Fall back to a safe message for network and non-JSON failures. */ }
    }
  }
  return 'We could not save or load your profile. Check your connection and try again.';
}

export function ProfileTargeting() {
  const api = useApiService();
  const [options, setOptions] = useState<ProfileOptions>();
  const [businessUnit, setBusinessUnit] = useState('');
  const [locale, setLocale] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const revision = useRef(0);
  const apply = (profile: ProfileOptions) => {
    setOptions(profile);
    setBusinessUnit(profile.businessUnits.includes(profile.businessUnit || '') ? profile.businessUnit! : '');
    setLocale(profile.locale || '');
  };
  const load = useCallback(async () => {
    const request = ++revision.current;
    setBusy(true); setError('');
    try {
      const profile = await api.get<ProfileOptions>('/notifications/profile');
      if (request === revision.current) apply(profile);
    } catch (e) { if (request === revision.current) setError(errorMessage(e)); }
    finally { if (request === revision.current) setBusy(false); }
  }, [api]);
  useEffect(() => { void load(); return () => { revision.current++; }; }, [load]);

  const save = async () => {
    const request = ++revision.current;
    setBusy(true); setError(''); setSaved(false);
    try {
      await api.put('/notifications/profile', { businessUnit, locale });
      const profile = await api.get<ProfileOptions>('/notifications/profile');
      if (request === revision.current) { apply(profile); setSaved(true); }
    } catch (e) { if (request === revision.current) setError(errorMessage(e)); }
    finally { if (request === revision.current) setBusy(false); }
  };
  const dirty = !!options && (businessUnit !== (options.businessUnit || '') || locale !== (options.locale || ''));

  return <Card aria-busy={busy}>
    <CardHeader>
      <CardTitle>Business unit and target group</CardTitle>
      <CardDescription>Choose your business unit and the country and language for relevant updates.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {busy && !options && <p role="status">Loading your notification profile…</p>}
      {error && <div role="alert" className="text-sm text-destructive">{error}{!options && <Button variant="outline" className="ml-3" disabled={busy} onClick={() => void load()}>Retry</Button>}</div>}
      <fieldset disabled={busy || !options} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="profile-business-unit">Business unit</Label>
          <select id="profile-business-unit" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={businessUnit} onChange={e => { setBusinessUnit(e.target.value); setSaved(false); }}>
            <option value="">Choose your business unit</option>
            {options?.businessUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-target-locale">Target group (country and language)</Label>
          <select id="profile-target-locale" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={locale} onChange={e => { setLocale(e.target.value); setSaved(false); }}>
            <option value="">Choose your country and language</option>
            {options?.locales.map(option => <option key={option.code} value={option.code}>{option.label} — {option.code}</option>)}
          </select>
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={busy || !options || !businessUnit || !locale || !dirty} onClick={() => void save()}>{busy && options ? 'Saving…' : 'Save profile'}</Button>
        {saved && <p role="status" className="text-sm text-muted-foreground">Your business unit and target group have been saved.</p>}
      </div>
    </CardContent>
  </Card>;
}
